import { GoogleGenerativeAI } from "@google/generative-ai";

// 🔐 Secure API Key retrieval (User must provide this in .env)
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

const genAI = API_KEY ? new GoogleGenerativeAI(API_KEY) : null;

const SYSTEM_INSTRUCTION = `You are "Sampark AI", the official digital intelligence of Bharat Connect.
Your goal is to provide accurate, concise, and professional answers about this messaging ecosystem.
Use Markdown for all formatting, including bold text, lists, and code blocks.
If you are unsure about a fact, state that you don't know rather than making it up.
Maintain a polite and neutral tone at all times.
Your mission is to provide helpful, creative, and India-aware assistance within this high-performance SaaS messaging grid.
Always refer to yourself as Sampark AI.`;

export const askAI = async (prompt, history = []) => {
  if (!genAI) {
    return "Sampark AI: Intelligence is currently offline. Please provide a valid Gemini API Key.";
  }

  // Preferred model (may be unavailable). Use fallback if needed
  // const PREFERRED_MODEL = "gemma-4-31b-it";
  const PREFERRED_MODEL = "gemini-3.1-flash-lite";
  const FALLBACK_MODEL = "gemma-4-31b-it";
  const MODEL_NAME = PREFERRED_MODEL;

  // 1. SDK Approach (Superior context handling)
  const trySDK = async () => {
    const model = genAI.getGenerativeModel({ 
      model: MODEL_NAME,
      systemInstruction: SYSTEM_INSTRUCTION,
      generationConfig: {
        temperature: 0.7,
        topP: 0.95,
        maxOutputTokens: 1024,
        responseMimeType: "text/plain"
      }
    });
    
    const chat = model.startChat({ 
      history: history.length > 0 ? history : []
    });
    
    const result = await chat.sendMessage(prompt);
    const response = await result.response;
    const text = response.text();
    if (!text || !text.trim()) {
      throw new Error("Empty response from model");
    }
    return text;
  };

  // 2. REST Fallback (Maximum network resilience)
  const tryREST = async () => {
    // Using v1beta for native system_instruction support
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${FALLBACK_MODEL}:generateContent?key=${API_KEY}`;
    
    const payload = {
      system_instruction: { parts: [{ text: SYSTEM_INSTRUCTION }] },
      contents: [
        ...history.map(h => ({
          role: h.role === "user" ? "user" : "model",
          parts: [{ text: h.parts[0].text }]
        })),
        { role: "user", parts: [{ text: prompt }] }
      ]
    };

    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.error?.message || response.statusText);
    }

    const data = await response.json();
    let text = data.candidates[0].content.parts[0].text;
      if (!text || !text.trim()) {
        return "I'm sorry, I couldn't generate a response. Please try again.";
      }
      return text;
  };

  try {
    return await trySDK();
  } catch (error) {
    console.warn("Sampark AI SDK failed, attempting v1beta REST fallback...", error);
    try {
      return await tryREST();
    } catch (restError) {
      console.error("Sampark AI Total Failure:", restError);
      
      const errorMsg = restError.message || "";
      if (errorMsg.toLowerCase().includes("fetch") || errorMsg.includes("network")) {
        return "ERROR_PREFIX Sampark AI Connectivity Shield: The Google API is blocked by your network. Please check your firewall.";
      }

      return `ERROR_PREFIX Sampark AI Alert: ${errorMsg}`;
    }
  }
};
